/**
 * Product Card Auto-Slideshow
 *
 * On desktop hover: cycles through product images with a progress bar
 * - 500ms for first slide, 1500ms for subsequent slides
 * - Progress bar grows from left to right
 * - Images crossfade
 */

(function() {
  'use strict';

  // Only run on devices with hover capability (skips phones/tablets, works on PCs with touchscreens)
  const hasHover = window.matchMedia('(hover: hover)').matches;
  if (!hasHover) return;

  const FIRST_SLIDE_DURATION = 500; // 500ms for first slide (quick start)
  const SLIDE_DURATION = 1500; // 1.5s for subsequent slides
  const TICK_INTERVAL = 20; // Progress bar update frequency

  class ProductCardSlideshow {
    constructor(container) {
      this.container = container;
      this.slides = container.querySelectorAll('[data-slideshow-slide]');
      this.progressBar = container.querySelector('[data-slideshow-progress]');

      if (this.slides.length <= 1) return;

      this.currentIndex = 0;
      this.elapsed = 0;
      this.interval = null;
      this.isPlaying = false;

      // Pre-calculate cumulative durations for each slide
      this.slideBoundaries = [];
      let cumulative = 0;
      for (let i = 0; i < this.slides.length; i++) {
        cumulative += i === 0 ? FIRST_SLIDE_DURATION : SLIDE_DURATION;
        this.slideBoundaries.push(cumulative);
      }
      this.totalDuration = cumulative;

      this.init();
    }

    init() {
      // Set initial state
      this.slides.forEach((slide, i) => {
        slide.style.opacity = i === 0 ? '1' : '0';
      });

      // Bind events to the product-item__image parent for better hover detection
      const productImage = this.container.closest('.product-item__image');
      const hoverTarget = productImage || this.container;

      hoverTarget.addEventListener('mouseenter', () => this.play());
      hoverTarget.addEventListener('mouseleave', () => this.stop());
    }

    play() {
      if (this.isPlaying) return;
      this.isPlaying = true;
      this.elapsed = 0;
      this.currentIndex = 0;

      this.interval = setInterval(() => this.tick(), TICK_INTERVAL);
    }

    stop() {
      this.isPlaying = false;
      clearInterval(this.interval);
      this.interval = null;

      // Reset to first slide
      this.currentIndex = 0;
      this.elapsed = 0;
      this.updateProgress();
      this.showSlide(0);
    }

    tick() {
      this.elapsed += TICK_INTERVAL;

      if (this.elapsed >= this.totalDuration) {
        // Loop back to start
        this.elapsed = 0;
        this.currentIndex = 0;
        this.showSlide(0);
        this.updateProgress();
        return;
      }

      // Determine which slide based on cumulative boundaries
      let newIndex = 0;
      for (let i = 0; i < this.slideBoundaries.length; i++) {
        if (this.elapsed < this.slideBoundaries[i]) {
          newIndex = i;
          break;
        }
      }

      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.showSlide(this.currentIndex);
      }

      this.updateProgress();
    }

    showSlide(index) {
      this.slides.forEach((slide, i) => {
        slide.style.opacity = i === index ? '1' : '0';
      });
    }

    updateProgress() {
      if (this.progressBar) {
        const percent = (this.elapsed / this.totalDuration) * 100;
        this.progressBar.style.setProperty('--slideshow-progress', `${percent}%`);
      }
    }
  }

  // Initialize all slideshows
  function initSlideshows() {
    const containers = document.querySelectorAll('[data-product-slideshow]:not([data-slideshow-init])');

    containers.forEach(container => {
      container.setAttribute('data-slideshow-init', 'true');
      new ProductCardSlideshow(container);
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlideshows);
  } else {
    initSlideshows();
  }

  // Re-init on dynamic content
  const observer = new MutationObserver(initSlideshows);
  observer.observe(document.body, { childList: true, subtree: true });
})();
